import BackendService from 'BackendService';
import { Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import type { ContentItem } from '../../models/ContentManagementInterface';
import type FundOpportunity from '../../models/FundOpportunity';
import type { FundUpdateEntry } from '../../models/FundUpdateEntry';
import {
  createContentPreviewHandlers,
  type ContentPreviewService,
} from '../admin/investorPreview/contentPreviewHandlers';
import InvestorPreviewFrame from '../admin/investorPreview/InvestorPreviewFrame';
import PreviewLoading from '../admin/investorPreview/PreviewLoading';
import {
  loadFundUpdatesAsPublished,
  type FundUpdatesAdminService,
} from '../admin/investorPreview/loadFundUpdatesAsPublished';
import MobileAppPreview from '../admin/MobileAppPreview';
import LibraryPanel from '../admin/newContentManagement/LibraryPanel';
import type { PageMode } from '../admin/newContentManagement/newContentManagementEditorTypes';

interface FundUpdatePagePreviewProps {
  fundOpportunity: FundOpportunity;
  pageMode: Exclude<PageMode, 'edit'>;
  /** Changes each time a preview is entered, so the latest updates and drafts are reloaded. */
  generation: number;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; entries: FundUpdateEntry[] };

/**
 * Fund Management's page-level preview (no single update open): every update's content as
 * investors would see it right after "Publish All" — on the web (the investor build, in an
 * isolated iframe) or in the app (the native renderer in a phone frame) — or the fund's library.
 */
export default function FundUpdatePagePreview({
  fundOpportunity,
  pageMode,
  generation,
}: FundUpdatePagePreviewProps) {
  const service = useMemo(() => new BackendService(), []);
  const handlers = useMemo(
    () => createContentPreviewHandlers(service as unknown as ContentPreviewService),
    [service],
  );
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const needsUpdates = pageMode !== 'library';

  useEffect(() => {
    if (!needsUpdates) return undefined;
    let cancelled = false;
    setLoad({ status: 'loading' });
    loadFundUpdatesAsPublished(service as unknown as FundUpdatesAdminService, fundOpportunity.id)
      .then((entries) => {
        if (!cancelled) setLoad({ status: 'ready', entries });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error', message: 'Unable to load the fund updates.' });
      });
    return () => {
      cancelled = true;
    };
  }, [service, fundOpportunity.id, generation, needsUpdates]);

  if (pageMode === 'library') {
    return <LibraryPanel fundOpportunityId={fundOpportunity.id} backendService={service} />;
  }
  if (load.status === 'loading') return <PreviewLoading />;
  if (load.status === 'error') {
    return (
      <Typography color="error" sx={{ py: 4 }}>
        {load.message}
      </Typography>
    );
  }

  if (pageMode === 'preview-app') {
    return (
      <MobileAppPreview
        content={[]}
        fundOpportunity={fundOpportunity}
        previewMode="fund-update"
        fundUpdates={load.entries.map((entry) => ({
          id: entry.externalId,
          dateLabel: entry.updateDate,
          content: entry.content as unknown as ContentItem[],
        }))}
      />
    );
  }
  return (
    <InvestorPreviewFrame
      view="fund-updates-content"
      viewProps={{
        entries: load.entries.map((entry) => ({
          date: entry.updateDate,
          title: entry.title,
          blocks: entry.content,
        })),
        generation,
      }}
      handlers={handlers}
      title="Fund updates as investors see them"
    />
  );
}
